import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import React, { useCallback, useMemo, useRef, useState } from "react";
import DatePicker from "react-date-picker";
import { useIntl } from "react-intl";
import PhoneInput from "react-phone-number-input";
import { Button, Col, Form, FormGroup, Input, Label, Row } from "reactstrap";
import { DEMO_REQUEST_LOADING } from "../../../constants/loading.constant";
import { useDemoCore } from "../../../cores/demo";
import { DemoChannel } from "../../../cores/demo/interfaces/DemoRequest";
import { useGlobalCore } from "../../../cores/globals";
import { LabelWembii } from "../LabelWembii";
import "./DemoRequest.scss";

interface Props {
  companyId?: string | number | null;
  companyName?: string;
  defaultEmail?: string;
  defaultPhone?: string;
  onSubmitted?: () => void;
  onSkip?: () => void;
  skipLabel?: string;
}

export default function DemoRequestForm(props: Props) {
  const {
    companyId,
    companyName,
    defaultEmail = "",
    defaultPhone = "",
    onSubmitted,
    onSkip,
    skipLabel,
  } = props;
  const intl = useIntl();
  const { createDemoRequest } = useDemoCore();
  const { isLoading } = useGlobalCore();

  const [email, setEmail] = useState(defaultEmail);
  const [phone, setPhone] = useState(defaultPhone);
  const [preferredDate, setPreferredDate] = useState<Date | null>(null);
  const [preferredTime, setPreferredTime] = useState("");
  const [channel, setChannel] = useState<DemoChannel>("call");
  const [message, setMessage] = useState("");
  const timeInputRef = useRef<HTMLInputElement>(null);

  const preferredAt = useMemo(() => {
    if (!preferredDate || !preferredTime) return "";
    return `${moment(preferredDate).format("YYYY-MM-DD")}T${preferredTime}`;
  }, [preferredDate, preferredTime]);

  const openNativeTimePicker = useCallback(() => {
    const el = timeInputRef.current;
    if (!el) return;
    try {
      const anyEl = el as HTMLInputElement & { showPicker?: () => void };
      if (typeof anyEl.showPicker === "function") {
        anyEl.showPicker();
        return;
      }
    } catch {
      // showPicker puede fallar si no hay gesto del usuario
    }
    el.focus();
    el.click();
  }, []);

  const isEmailValid = useMemo(() => {
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailRegex.test(email.trim());
  }, [email]);

  const loading = isLoading(DEMO_REQUEST_LOADING);
  const disabled = !email.trim() || !isEmailValid || !channel || loading;

  const onFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await createDemoRequest(
      {
        companyId: companyId ?? null,
        companyName,
        email: email.trim(),
        phone,
        preferredAt,
        channel,
        message: message.trim(),
      },
      onSubmitted
    );
  };

  return (
    <Form onSubmit={onFormSubmit} className="demo-request-form">
      <FormGroup>
        <LabelWembii forid="demo-email" required>
          {intl.formatMessage({ id: "app.emailTxt" })}
        </LabelWembii>
        <Input
          type="text"
          name="email"
          id="demo-email"
          autoComplete="email"
          invalid={!!email.trim() && !isEmailValid}
          placeholder={intl.formatMessage({ id: "app.emailTxt" })}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </FormGroup>
      <FormGroup>
        <LabelWembii forid="demo-phone">
          {intl.formatMessage({ id: "app.phoneTxt" })}
        </LabelWembii>
        <PhoneInput
          className="demo-request-form__phone"
          name="phone"
          id="demo-phone"
          placeholder={intl.formatMessage({ id: "app.phoneTxt" })}
          value={phone}
          onChange={(v) => setPhone(v || "")}
        />
      </FormGroup>
      <FormGroup>
        <Label className="demo-request-form__label">
          {intl.formatMessage({ id: "app.demo.form.preferredAt" })}
        </Label>
        <Row className="demo-request-form__datetime-row">
          <Col xs={12} sm={7} className="demo-request-form__datetime-col">
            <span className="demo-request-form__datetime-sublabel">
              {intl.formatMessage({ id: "app.programModalPost.Date" })}
            </span>
            <div className="demo-request-form__date-picker" id="demo-preferredDate">
              <DatePicker
                className="w-100"
                format="dd/MM/y"
                value={preferredDate}
                minDate={new Date()}
                clearIcon={null}
                calendarIcon={null}
                onChange={(value: Date | null) => setPreferredDate(value)}
              />
              <div className="demo-request-form__field-icon" aria-hidden>
                <FontAwesomeIcon icon={["far", "calendar"]} />
              </div>
            </div>
          </Col>
          <Col xs={12} sm={5} className="demo-request-form__datetime-col">
            <span className="demo-request-form__datetime-sublabel">
              {intl.formatMessage({ id: "app.programModalPost.time" })}
            </span>
            <div
              className="demo-request-form__time-picker"
              role="presentation"
              onClick={(e) => {
                if (e.target === timeInputRef.current) return;
                openNativeTimePicker();
              }}
            >
              <input
                ref={timeInputRef}
                type="time"
                id="demo-preferredTime"
                name="preferredTime"
                className="demo-request-form__time-input"
                aria-label={intl.formatMessage({ id: "app.programModalPost.time" })}
                value={preferredTime}
                onChange={(e) => setPreferredTime(e.target.value)}
                onClick={() => openNativeTimePicker()}
              />
              <div className="demo-request-form__field-icon" aria-hidden>
                <FontAwesomeIcon icon={["far", "clock"]} />
              </div>
            </div>
          </Col>
        </Row>
      </FormGroup>
      <FormGroup>
        <Label for="demo-channel" className="demo-request-form__label">
          {intl.formatMessage({ id: "app.demo.form.channel" })}
        </Label>
        <Input
          type="select"
          name="channel"
          id="demo-channel"
          value={channel}
          onChange={(e) => setChannel(e.target.value as DemoChannel)}
        >
          <option value="call">
            {intl.formatMessage({ id: "app.demo.channel.call" })}
          </option>
          <option value="video">
            {intl.formatMessage({ id: "app.demo.channel.video" })}
          </option>
          <option value="whatsapp">
            {intl.formatMessage({ id: "app.demo.channel.whatsapp" })}
          </option>
        </Input>
      </FormGroup>
      <FormGroup>
        <Label for="demo-message" className="demo-request-form__label">
          {intl.formatMessage({ id: "app.demo.form.message" })}
        </Label>
        <Input
          type="textarea"
          name="message"
          id="demo-message"
          rows={2}
          placeholder={intl.formatMessage({ id: "app.demo.form.messagePlaceholder" })}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
      </FormGroup>
      <Button color="primary" block disabled={disabled} className="demo-request-form__submit">
        {intl.formatMessage({ id: "app.demo.form.submit" })}
      </Button>
      {onSkip && (
        <Button
          type="button"
          color="link"
          block
          className="demo-request-form__skip"
          onClick={onSkip}
          disabled={loading}
        >
          {skipLabel || intl.formatMessage({ id: "app.demo.form.skip" })}
        </Button>
      )}
    </Form>
  );
}
